import * as Yup from 'yup';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment, AlertTitle, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import useLocales from '../../../hooks/useLocales';

// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../../components/hook-form';

// ----------------------------------------------------------------------

type FormValuesProps = {
  email: string;
  password: string;
  remember: boolean;
  afterSubmit?: string;
};

export default function LoginForm() {
  const { translate } = useLocales();
  const { login } = useAuth();
  const navigate = useNavigate();

  const holdRemember: string = translate("loginPage.remember")
  const isMountedRef = useIsMountedRef();

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false);


  const LoginSchema = Yup.object().shape({
    email: Yup.string().email(translate("loginPage.adornamentValidEmail")).required(translate("loginPage.adornamentEmailRequired")),
    password: Yup.string().required(translate("loginPage.adornamentPasswordRequired")),
  });

  var defaultValues = {
    email: email,
    password: password,
    remember: true,
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    setEmail(data.email)
    setPassword(data.password)
    var response = await login(data.email, data.password);

    if (isMountedRef.current && response && response.status !== 200) {
      if(response.status === 401){
        setError('afterSubmit', { ...response, message: translate("loginPage.loginErrorWrongPassword") });
      }
      if(response.status === 404){
        setError('afterSubmit', { ...response, message: translate("loginPage.loginErrorUserNotFound") });
      }
      if(response.status === 500){
        setError('afterSubmit', { ...response, message: translate("loginPage.loginErrorNoConnection") });
      }
      
    }else{
      navigate("/",{replace:true})
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField name="email" label={translate("loginPage.email")}/>

        <RHFTextField
          name="password"
          label={translate("loginPage.password")}
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Box mb={2}/>

      {/* <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        <RHFCheckbox name="remember" label={holdRemember} />
        <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword}>
         {translate("loginPage.forgot")}
        </Link>
      </Stack> */}

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        {translate("loginPage.loginButton")}
      </LoadingButton>
    </FormProvider>
  );
}
